import { TitleInput } from "@/components/ui/title-input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { LinkIcon } from "lucide-react";
import { useState } from "react";
import type React from "react";
import type { ComponentProps, FocusEvent } from "react";
import { Button } from "./button";

type TitleInputWithLinkProps = ComponentProps<typeof TitleInput> & {
  containerClassName?: string;
  overlayClassName?: string;
  titleClassName?: string;
  linkClassName?: string;
  linkAriaLabel?: string;
  copyText?: string;
  extraActions?: React.ReactNode;
  /**
   * CR-97: paint the placeholder like the title itself (text-foreground)
   * instead of as a muted hint. Message rooms show their derived default
   * title ("All project members", the participant list) through the
   * placeholder — it IS the title until a user types one. Only the
   * not-editing overlay is affected: while the field has focus the native
   * placeholder stays muted on purpose, to read as "a default you can
   * replace".
   */
  placeholderAsTitle?: boolean;
};

export function TitleInputWithLink({
  containerClassName,
  overlayClassName,
  titleClassName,
  linkClassName,
  linkAriaLabel = "Copy link",
  copyText,
  extraActions,
  placeholderAsTitle = false,
  className,
  onFocus,
  onBlur,
  value,
  placeholder,
  ...props
}: TitleInputWithLinkProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const titleText = value?.toString() ?? "";

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setIsEditing(true);
    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setIsEditing(false);
    onBlur?.(event);
  };

  const handleCopy = () => {
    const textToCopy = copyText ?? window.location.href;
    setIsCopied(true);
    window.setTimeout(() => {
      setIsCopied(false);
    }, 1000);
    void navigator.clipboard.writeText(textToCopy);
  };

  return (
    <div className={cn("group relative min-h-9 flex-1", containerClassName)}>
      <TitleInput
        value={value}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={cn(
          // CR-47: font-display (Zilla Slab) on the title. It must be on BOTH
          // the input and the overlay spans below — this component swaps
          // between them on focus, and a face mismatch would make the text
          // jump the moment the field is clicked. Listed AFTER the caller's
          // className so tailwind-merge cannot let an override strip the face.
          "h-9 w-full border-none bg-transparent text-3xl font-semibold focus:ring-0 focus:outline-none",
          isEditing
            ? "placeholder-muted-foreground"
            : "text-transparent placeholder-transparent caret-transparent",
          className,
          "font-display",
        )}
        {...props}
      />
      {!isEditing ? (
        <div
          className={cn("pointer-events-none absolute inset-0 flex items-center", overlayClassName)}
        >
          <div className="flex w-full min-w-0 items-center">
            {titleText ? (
              <span
                title={titleText}
                className={cn(
                  "text-foreground truncate text-3xl font-semibold",
                  titleClassName,
                  "font-display",
                )}
              >
                {titleText}
              </span>
            ) : placeholder ? (
              <span
                title={placeholder}
                className={cn(
                  "truncate text-3xl font-semibold",
                  placeholderAsTitle ? "text-foreground" : "text-muted-foreground",
                  titleClassName,
                  "font-display",
                )}
              >
                {placeholder}
              </span>
            ) : null}
            <div className="shrink-0 pl-2">
              <Tooltip open={isHovering}>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    aria-label={isCopied ? "Copied!" : linkAriaLabel}
                    variant="ghost-muted"
                    className={linkClassName}
                    onClick={handleCopy}
                    onPointerEnter={() => setIsHovering(true)}
                    onPointerLeave={() => setIsHovering(false)}
                    onFocus={() => setIsHovering(true)}
                    onBlur={() => setIsHovering(false)}
                  >
                    <LinkIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{isCopied ? "Copied!" : "Copy link"}</TooltipContent>
              </Tooltip>
            </div>
            {extraActions}
          </div>
        </div>
      ) : null}
    </div>
  );
}
